/* import __COLOCATED_TEMPLATE__ from './title-changed.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type TitleChanged from 'embercom/objects/inbox/renderable/title-changed';
import LinkWithText from 'embercom/helpers/link-with-text';
import { createSafeAdminLink } from 'embercom/utils/admin-link';
import {
  truncateEventText,
  isEventTextTruncated,
  FEATURE_FLAG_NAME,
} from 'embercom/lib/inbox2/event-text-truncation';

interface TitleChangedPart extends RenderablePart {
  renderableData: TitleChanged;
}

interface Args {
  part: TitleChangedPart;
}

interface Signature {
  Args: Args;
}

export default class Inbox2ConversationStreamEventPartsTitleChangedComponent extends Component<Signature> {
  @service declare session: Session;

  // @ts-ignore helper doesn't seem to be typed correctly
  linkWithTextHelper = LinkWithText.create(this);

  get renderableData() {
    return this.args.part.renderableData;
  }

  get teammate() {
    return this.renderableData.adminSummary;
  }

  get translationKey(): string {
    let isCurrentUser = this.teammate.id === this.session.teammate.id;

    if (this.isConversationEventsUiImprovementsEnabled) {
      return isCurrentUser
        ? 'inbox.title-changed.you-changed-title-v2'
        : 'inbox.title-changed.admin-changed-title-v2';
    }
    return isCurrentUser
      ? 'inbox.title-changed.you-changed-title'
      : 'inbox.title-changed.admin-changed-title';
  }

  get translationData() {
    let title =
      this.isConversationEventsUiImprovementsEnabled && this.isTitleTruncated
        ? this.truncatedTitle
        : this.renderableData.title;

    if (this.teammate.id === this.session.teammate.id) {
      return {
        data: {
          value: title,
        },
      };
    } else {
      return {
        data: {
          who: createSafeAdminLink(this.teammate, this.linkWithTextHelper),
          value: title,
        },
      };
    }
  }

  get isConversationEventsUiImprovementsEnabled() {
    return this.session.workspace?.isFeatureEnabled(FEATURE_FLAG_NAME) ?? false;
  }

  get truncatedTitle(): string {
    return truncateEventText(this.renderableData.title);
  }

  get isTitleTruncated(): boolean {
    return isEventTextTruncated(this.renderableData.title);
  }

  get fullTitle(): string {
    return this.renderableData.title;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::TitleChanged': typeof Inbox2ConversationStreamEventPartsTitleChangedComponent;
    'inbox2/conversation-stream/event-parts/title-changed': typeof Inbox2ConversationStreamEventPartsTitleChangedComponent;
  }
}
